import type {ReactNode, ReactElement, HTMLAttributes} from 'react'
import {useState, useRef, useEffect, Children, cloneElement} from 'react'
import {createPortal} from 'react-dom'
import FocusTrapReact from 'focus-trap-react'
import {CSSTransition} from 'react-transition-group'
import Image from 'next/image'
import type {Elements} from '@kontent-ai/delivery-sdk'
import {loadImageFromKontentAI} from '@/image-loader'
import {ButtonIcons} from '../../../../components/button-icons/button-icon'
import {LargeVisualBlock} from '../../blocks/large-visual-block/large-visual-block'
import {useWindowSize} from '../../../../utilities/hooks/use-window-size'
import {EllipseButton} from '../button/ellipse-button'
import {YoutubeVideo} from '../you-tube-video'
import {Wistia} from '../wistia/wistia'
import {Button} from '../button/button'

const VIDEO_TYPES = {
	YOUTUBE: 'youtube',
	WISTIA: 'wistia',
} as const

export type VideoType = (typeof VIDEO_TYPES)[keyof typeof VIDEO_TYPES]

interface MediaCardProps {
	className?: string
	children?: ReactNode
	title: ReactNode
	subtitle?: ReactNode
	leadingTitle?: boolean
	videoPosterFrameUrl?: string
	videoId?: string | Elements.TextElement
	videoType?: VideoType
	metaBlock?: React.ReactElement<
		HTMLAttributes<HTMLElement> & {blur?: true | false}
	> | null
	videoPosterAltText?: string | null
	closeBtnText?: string
	playBtnText?: string
	hasPlayVideoButton?: boolean
	videoElement?: ReactNode
}

export const MediaCard = ({
	className = '',
	leadingTitle = false,
	title,
	subtitle = '',
	videoPosterFrameUrl = '',
	videoPosterAltText = '',
	videoId = '',
	videoElement,
	videoType = VIDEO_TYPES.YOUTUBE,
	children = null,
	metaBlock = null,
	closeBtnText = 'Close',
	playBtnText = 'Play',
	hasPlayVideoButton = false,
}: MediaCardProps): ReactElement => {
	const childrenArray = Children.toArray(children) as ReactElement[]
	const btns = childrenArray.filter(
		(item) => item.type === Button
	) as ReactElement<{onClick: (() => void) | null; href: string | null}>[]
	const content = childrenArray.filter(
		(item) =>
			item.type !== Button &&
			item.type !== Image &&
			item.type !== 'img' &&
			item.type !== LargeVisualBlock
	)
	const [showVideoPopup, setShowVideoPopup] = useState(false)
	const [currentVideoId, setCurrentVideoId] = useState(videoId) // Track the current video ID
	const toggleVideoPopup = (): void => {
		setShowVideoPopup((prev) => !prev)
	}
	const windowSize = useWindowSize()

	const popupRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (currentVideoId !== videoId) {
			setCurrentVideoId(videoId)
		}
	}, [videoId, currentVideoId, videoType])

	const getPopupPortal = (): ReactElement | null => {
		if (!windowSize.width) {
			return null
		}

		const renderVideo = (): ReactElement | null => {
			switch (videoType) {
				case VIDEO_TYPES.YOUTUBE:
					return (
						<YoutubeVideo
							controls={1}
							pauseHandler={() => null}
							videoId={currentVideoId as string}
						/>
					)
				case VIDEO_TYPES.WISTIA:
					return <Wistia autoPlay id={currentVideoId as string} />
				default:
					return null
			}
		}

		return createPortal(
			<CSSTransition
				classNames="popup"
				in={showVideoPopup}
				nodeRef={popupRef}
				onEntered={() => null}
				timeout={500}
				unmountOnExit
			>
				<FocusTrapReact>
					<div
						className="fixed left-0 top-0 z-100 flex h-full w-full items-center bg-blue-transparent py-20 md:px-12"
						onClick={toggleVideoPopup}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								toggleVideoPopup()
							}
						}}
						ref={popupRef}
						role="button"
						tabIndex={0}
					>
						<Button
							as="button"
							className="absolute right-0 top-0 z-10 mr-4 mt-4 bg-blue-800 text-white hover:bg-blue-900"
							icon={ButtonIcons.Close}
							onClick={(e) => {
								e.stopPropagation()
								toggleVideoPopup()
							}}
							reversed
							variant=""
						>
							{closeBtnText}
						</Button>
						<div className="container-wide popup-container flex h-full items-center">
							{renderVideo()}
						</div>
					</div>
				</FocusTrapReact>
			</CSSTransition>,
			document.body
		)
	}
	return (
		<>
			<div
				className={`mx-2 flex max-w-sm flex-col overflow-hidden rounded-lg bg-white shadow-md ${className}`}
			>
				{leadingTitle && title ? (
					<div className="px-6 py-3">
						<h3>{title}</h3>
					</div>
				) : null}
				<div className="relative">
					{metaBlock
						? cloneElement(metaBlock, {
								className: `${
									metaBlock.props.className ?? ''
								} absolute top-0 z-10`,
								blur: metaBlock.props.blur !== false,
							})
						: null}
					{!videoId || !videoPosterFrameUrl ? (
						<div className="no-video-placeholder">
							{videoElement ? 'Video not available' : ''}
						</div>
					) : (
						<>
							<Image
								alt={videoPosterAltText || ''}
								className="h-48 w-full object-cover"
								height={200}
								loader={loadImageFromKontentAI}
								src={videoPosterFrameUrl}
								width={200}
							/>
							<div className="absolute inset-0 z-10 flex items-center justify-center">
								<EllipseButton
									as="button"
									icon={
										<svg
											className="pl-1"
											viewBox="0 0 15.954 18.492"
										>
											<path
												d="M8.381,1.493a1,1,0,0,1,1.73,0l7.511,12.96a1,1,0,0,1-.865,1.5H1.735a1,1,0,0,1-.865-1.5Z"
												transform="translate(15.954) rotate(90)"
											/>
										</svg>
									}
									iconSize="w-6 h-6"
									onClick={toggleVideoPopup}
									size="md"
									title={playBtnText}
								/>
							</div>
						</>
					)}
				</div>

				<div className="flex h-full flex-col px-5 py-5 [&>ol]:list-[auto] [&>ul]:list-disc">
					{subtitle ? (
						<p className="content content--no-width-restriction mb-2 text-sm">
							{subtitle}
						</p>
					) : null}
					{!leadingTitle && title ? (
						<h3 className="mb-2 text-xl">{title}</h3>
					) : null}
					{content}
					<div className={btns.length ? 'mt-auto pt-8' : ''}>
						{videoId
							? btns.map((btn, index) =>
									cloneElement(
										btn,
										(index === 0 &&
											btn.props.onClick === null &&
											btn.props.href === null) ||
											hasPlayVideoButton
											? {
													onClick: toggleVideoPopup,
												}
											: undefined
									)
								)
							: btns}
					</div>
				</div>
			</div>

			{getPopupPortal()}
		</>
	)
}
